import { ref } from 'vue'
import { sleep } from '@/utils/helpers'

// The extension content script replies to this event unconditionally (regardless
// of its own auth state) and with no side effects, so it is a safe presence probe.
const PROBE_EVENT = 'rubra:auth:loaded'
const PROBE_REPLY_EVENT = 'rubra:auth:loaded;reply'
const PROBE_ATTEMPTS = 5
const PROBE_INTERVAL_MS = 500

/**
 * Detects whether the rubra browser extension is installed by handshaking with
 * its content script. Resolving "not installed" takes up to
 * PROBE_ATTEMPTS * PROBE_INTERVAL_MS (~2.5s), so callers should keep any UI
 * hidden until `checkPresence()` resolves to avoid a flash.
 *
 * @returns {{ detected: import('vue').Ref<boolean>, checking: import('vue').Ref<boolean>, checkPresence: () => Promise<boolean> }}
 */
export function useExtensionPresence() {
  const detected = ref(false)
  const checking = ref(false)

  const checkPresence = async () => {
    checking.value = true
    try {
      for (let attempt = 0; attempt < PROBE_ATTEMPTS; attempt += 1) {
        let replied = false
        const listener = () => { replied = true }

        window.addEventListener(PROBE_REPLY_EVENT, listener, { once: true })
        window.dispatchEvent(new CustomEvent(PROBE_EVENT))

        // eslint-disable-next-line no-await-in-loop
        await sleep(PROBE_INTERVAL_MS)
        window.removeEventListener(PROBE_REPLY_EVENT, listener)

        if (replied) {
          detected.value = true
          return true
        }
      }

      detected.value = false
      return false
    } finally {
      checking.value = false
    }
  }

  return { detected, checking, checkPresence }
}
